import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy, Check, Share2 } from 'lucide-react';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareUrl: string;
  title?: string;
}

export function ShareModal({ isOpen, onClose, shareUrl, title }: ShareModalProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copied]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Overlay */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black bg-opacity-50"
        />

        {/* Modal */}
        {/* 2.0.0: 添加暗色主题支持 - Modal 容器背景和阴影 */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative bg-white dark:bg-gray-950 rounded-xl shadow-2xl dark:shadow-gray-900/50 w-full max-w-md p-6 z-10"
        >
          {/* Close Button */}
          {/* 2.0.0: 添加暗色主题支持 - 关闭按钮悬停状态和图标颜色 */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>

          {/* Content */}
          <div className="space-y-4">
            {/* Icon */}
            {/* 2.0.0: 添加暗色主题支持 - 图标圆形背景 */}
            <div className="flex justify-center">
              <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                <Share2 className="w-8 h-8 text-orange-500" />
              </div>
            </div>

            {/* Title */}
            {/* 2.0.0: 添加暗色主题支持 - 标题和描述文字颜色 */}
            <div className="text-center">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                {title || t('shareModalTitle', '分享链接')}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('shareModalDescription', '复制链接分享给其他人')}
              </p>
            </div>

            {/* Share URL */}
            {/* 2.0.0: 添加暗色主题支持 - URL 输入区域背景、边框和文字颜色 */}
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="flex-1 text-sm text-gray-700 dark:text-gray-300 bg-transparent border-none outline-none truncate"
                />
                {/* 2.0.0: 添加暗色主题支持 - 已复制状态背景和文字颜色 */}
                <button
                  onClick={handleCopy}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    copied
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                      : 'bg-orange-500 text-white hover:bg-orange-600'
                  }`}
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      {t('shareModalCopied', '已复制')}
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      {t('shareModalCopy', '复制')}
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            {/* 2.0.0: 添加暗色主题支持 - 关闭按钮背景、悬停状态和文字颜色 */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition-colors"
              >
                {t('shareModalClose', '关闭')}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

